import type { ReportGroup } from "./ReportView";

/** One resolved party (conductor, beneficiary, requester) or any other
 *  nested group. The group's label already carries the party's name --
 *  resolve_report() inlines it from definitions[] at the reference site.
 *
 *  A field's `value` is always a non-empty display string except for a
 *  genuinely blank scalar (report_view.py's `_format_scalar(None)` -> ""),
 *  which is why "—" is the fallback here rather than "(none)": an empty
 *  *list* is rendered by the backend as the literal string "(none)" (see
 *  report_view.py's `_add`), and that string is deliberately left to print
 *  as-is -- it already reads correctly next to its field label ("Related
 *  reports: (none)"), and there is no honest way to tell it apart from a
 *  genuine free-text value of "(none)" using `value` alone anyway (`raw`
 *  carries that distinction -- `[]` vs the string -- but nothing here needs
 *  to act on it, so it is left unused). */
export function PartyCard({ group, depth = 0 }: { group: ReportGroup; depth?: number }) {
  return (
    <div className={depth === 0 ? "report-group" : "report-group nested"}>
      <h5>{group.label}</h5>
      {group.fields.length > 0 && (
        <dl className="report-fields">
          {group.fields.map((f) => (
            <div key={f.path} className="report-field">
              <dt>{f.label}</dt>
              <dd>{f.value || "—"}</dd>
            </div>
          ))}
        </dl>
      )}
      {group.groups.map((sub) => (
        <PartyCard key={sub.path + sub.label} group={sub} depth={depth + 1} />
      ))}
    </div>
  );
}
